import { myinfoQueries, reservationQueries } from "@app/config/queryFactory";
import { ERROR_CODES, HTTP_STATUS } from "@base/lib/error";
import { isAxiosApiError } from "@base/lib/utils";
import { useOverlay } from "@base/model/hooks/useOverlay";
import { membershipQueries } from "@features/membership/api/queryFactory";
import type { PaymentMessage } from "@features/payment/model/types";
import { createMeetingReservation } from "@features/reservation/api/meeting";
import { ReservationAlert } from "@features/reservation/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CreateMeetingReservationRequest } from "../types";

const serviceUrl = typeof window !== "undefined" ? window.location.origin : "";

const ALLOWED_ORIGIN = window.location.origin;

const useCreateReservationPayment = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { open } = useOverlay();

  const paymentWindowRef = useRef<Window | null>(null);

  const [paymentId, setPaymentId] = useState<number | null>(null);

  const handleOpenSilverMembershipSameDayReservationNotAllowedDialog = () => {
    open(({ isOpen, close }) => (
      <ReservationAlert
        isOpen={isOpen}
        onClose={close}
        title={t("reservationUnavailableTitle")}
        description={t("silverMembershipSameDayReservationNotAllowed")}
      />
    ));
  };

  const handleOpenOnlySilverMembershipReservationDialog = () => {
    open(({ isOpen, close }) => (
      <ReservationAlert
        isOpen={isOpen}
        onClose={close}
        title={t("reservationUnavailableTitle")}
        description={t("onlySilverMembershipReservation")}
      />
    ));
  };

  const handleOpenReservationLimitReachedDialog = () => {
    open(({ isOpen, close }) => (
      <ReservationAlert
        isOpen={isOpen}
        onClose={close}
        title={t("reservationLimitReachedTitle")}
        description={t("reservationLimitReachedDescription")}
      />
    ));
  };

  const handleOpenMeetingRoomAlreadyReservedDialog = () => {
    open(({ isOpen, close }) => (
      <ReservationAlert
        isOpen={isOpen}
        onClose={close}
        title={t("meetingRoomAlreadyReservedTitle")}
        description={t("meetingRoomAlreadyReservedDescription")}
      />
    ));
  };

  const paymentMutation = useMutation({
    mutationFn: (data: CreateMeetingReservationRequest) => createMeetingReservation(data),
    onSuccess: (data) => {
      if (data.hosted_page) {
        // 유료 결제
        const redirectUrl = encodeURIComponent(data.hosted_page);

        const newWindow = window.open(`${serviceUrl}/payment/callback?redirectUrl=${redirectUrl}`);

        if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
          toast.error(t("apply.payment.failedToOpenPaymentWindow"));
          return;
        }

        paymentWindowRef.current = newWindow;
        setPaymentId(data.payment_id);
      } else {
        // 무료 크레딧으로 결제된 경우
        queryClient.invalidateQueries({ queryKey: reservationQueries.all(), refetchType: "all" });
        // 과거 예약 이력이 생겼으므로 예약 탭 노출 조건 재조회
        queryClient.invalidateQueries({ queryKey: myinfoQueries.hasPastReservationKey(), refetchType: "all" });
        toast.success(t("reservationCreatedSuccess"));
      }
    },
    onError: (error) => {
      if (isAxiosApiError(error)) {
        const { code, status } = error.response.data;

        if (status === HTTP_STATUS.BAD_REQUEST) {
          switch (code) {
            // 멤버십 자격 관련 에러 → 다이얼로그 안내
            case ERROR_CODES.SILVER_MEMBERSHIP_SAME_DAY_RESERVATION_NOT_ALLOWED:
              handleOpenSilverMembershipSameDayReservationNotAllowedDialog();
              return;
            case ERROR_CODES.ONLY_SILVER_MEMBERSHIP_RESERVATION:
            case ERROR_CODES.SILVER_MEMBERSHIP_RESERVATION_NOT_ALLOWED:
              handleOpenOnlySilverMembershipReservationDialog();
              return;
            case ERROR_CODES.DAILY_MEETING_ROOM_USAGE_EXCEEDED:
              handleOpenReservationLimitReachedDialog();
              return;
            case ERROR_CODES.MEETING_ROOM_ALREADY_RESERVED:
              handleOpenMeetingRoomAlreadyReservedDialog();
              return;
            // 입력값 검증 에러 → 코드별 토스트 안내
            case ERROR_CODES.INVALID_TIME_RANGE:
              toast.error(t("invalidTimeRange"));
              return;
            case ERROR_CODES.REQUIRED_FIELD_MISSING:
              toast.error(t("requiredFieldMissing"));
              return;
            case ERROR_CODES.INVALID_DATE_FORMAT:
              toast.error(t("invalidDateFormat"));
              return;
            case ERROR_CODES.INVALID_TIME_FORMAT:
              toast.error(t("invalidTimeFormat"));
              return;
          }
        }

        toast.error(t("reservationCreateFailed"));
      }
    },
  });

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // (A) Origin 검증
      if (event.origin !== ALLOWED_ORIGIN) return;

      // (B) Source 검증: 우리가 연 팝업이 보낸 것인지 확인
      //     팝업이 차단/재생성되면 달라질 수 있으니 상황에 따라 완화 가능
      if (!paymentWindowRef.current || event.source !== paymentWindowRef.current) return;

      const data = event.data as PaymentMessage;
      if (!data || typeof data !== "object" || !("type" in data)) return;

      // (C) 메시지 타입별 처리
      if (data.type === "PAYMENT_SUCCEEDED" && data.paymentId === paymentId?.toString()) {
        toast.success(t("reservationCreatedSuccess"));

        queryClient.invalidateQueries({ queryKey: myinfoQueries.profileKey(), refetchType: "all" });
        queryClient.invalidateQueries({ queryKey: membershipQueries.membershipKey(), refetchType: "all" });
        queryClient.invalidateQueries({ queryKey: reservationQueries.all(), refetchType: "all" });
        // 과거 예약 이력이 생겼으므로 예약 탭 노출 조건 재조회
        queryClient.invalidateQueries({ queryKey: myinfoQueries.hasPastReservationKey(), refetchType: "all" });
      }

      if (data.type === "PAYMENT_FAILED") {
        toast.error(t("apply.payment.failedToProcessPayment"));
      }

      paymentWindowRef.current = null;

      // (D) 정리
      // try {
      //   paymentWindowRef.current?.close();
      // } catch {}
      // paymentWindowRef.current = null;
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [paymentId, t, queryClient]);

  return paymentMutation;
};

export default useCreateReservationPayment;
